import React from 'react';
import { formatDate } from '@koob/dates';
import { isArray, isBoolean, IGNORED_FIELDS } from '../helpers';
import { ModifiedBadge, ValueRenderer, FieldLabel } from '../displays';
import ArrayDiff from './ArrayDiff';
import AvailableDaysDiff from './AvailableDaysDiff';
import BooleanDiff from './BooleanDiff';
import PrimitiveDiff from './PrimitiveDiff';

// Fields to exclude from diff display (shown in header or summary)
const EXCLUDED_DIFF_FIELDS = ['id', 'startAt', 'endAt'];

// Fields to display in the period summary (in order) - same as PeriodDisplay
const PERIOD_SUMMARY_FIELDS = [
  'state',
  'price',
  'allotments',
  'minPax',
  'maxPax',
  'autoConfirmEnabled',
  'autoConfirmDays',
];

// Boolean fields that should be shown even when null
const BOOLEAN_FIELDS = ['autoConfirmEnabled'];

export default function PeriodDiff({
  beforePeriod,
  afterPeriod,
  labels,
  attachmentLabels,
}) {
  const period = afterPeriod || beforePeriod;
  const dateRange = `${formatDate(period.startAt, 'D MMM YYYY')} - ${formatDate(period.endAt, 'D MMM YYYY')}`;

  // Find changed fields
  const allKeys = [
    ...new Set([
      ...Object.keys(beforePeriod || {}),
      ...Object.keys(afterPeriod || {}),
    ]),
  ];

  // Changed fields excluding summary fields (which are shown separately)
  const changedFields = allKeys
    .filter((key) => !EXCLUDED_DIFF_FIELDS.includes(key))
    .filter((key) => !IGNORED_FIELDS.includes(key))
    .filter((key) => !PERIOD_SUMMARY_FIELDS.includes(key))
    .filter((key) => {
      const beforeVal = beforePeriod?.[key];
      const afterVal = afterPeriod?.[key];
      return JSON.stringify(beforeVal) !== JSON.stringify(afterVal);
    });

  // Summary fields that exist and have changed
  const changedSummaryFields = PERIOD_SUMMARY_FIELDS.filter((field) => {
    const beforeVal = beforePeriod?.[field];
    const afterVal = afterPeriod?.[field];
    return JSON.stringify(beforeVal) !== JSON.stringify(afterVal);
  });

  // Summary fields to display (from afterPeriod, excluding changed ones which will show diff)
  // For boolean fields, show even if null (to display empty circle)
  const unchangedSummaryFields = PERIOD_SUMMARY_FIELDS.filter((field) => {
    if (changedSummaryFields.includes(field)) return false;
    if (BOOLEAN_FIELDS.includes(field)) {
      return field in period; // Show if field exists (even if null)
    }
    const value = period[field];
    return value !== undefined && value !== null;
  });

  return (
    <div className="p-2 bg-gray-50 rounded border border-amber-200">
      <div className="flex items-center gap-2">
        <i className="fa-regular fa-calendar-range text-indigo-500" />
        <span className="text-sm font-medium text-gray-700">{dateRange}</span>
        <ModifiedBadge />
      </div>

      {/* Summary fields (unchanged ones shown as context) */}
      {unchangedSummaryFields.length > 0 && (
        <div className="ml-6 mt-1 space-y-0.5">
          {unchangedSummaryFields.map((field) => (
            <div key={field}>
              <FieldLabel name={field} />
              <ValueRenderer value={period[field]} fieldName={field} />
            </div>
          ))}
        </div>
      )}

      {/* Changed summary fields with diff */}
      {changedSummaryFields.length > 0 && (
        <div className="ml-6 mt-2 space-y-1">
          {changedSummaryFields.map((key) => {
            const beforeVal = beforePeriod?.[key];
            const afterVal = afterPeriod?.[key];
            // Use BooleanDiff for boolean fields (including null values)
            const isBooleanField =
              BOOLEAN_FIELDS.includes(key) ||
              isBoolean(beforeVal) ||
              isBoolean(afterVal);
            return (
              <div key={key}>
                <FieldLabel name={key} />
                {isBooleanField ? (
                  <BooleanDiff beforeValue={beforeVal} afterValue={afterVal} />
                ) : (
                  <PrimitiveDiff
                    beforeValue={beforeVal}
                    afterValue={afterVal}
                    fieldName={key}
                    labels={labels}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Other changed fields (non-summary) */}
      {changedFields.length > 0 && (
        <div className="ml-6 mt-2 space-y-2">
          {changedFields.map((key) => {
            const beforeVal = beforePeriod?.[key];
            const afterVal = afterPeriod?.[key];
            return (
              <div key={key}>
                <FieldLabel name={key} />
                {key === 'availableDays' ? (
                  <AvailableDaysDiff
                    beforeValue={beforeVal}
                    afterValue={afterVal}
                  />
                ) : isArray(beforeVal) || isArray(afterVal) ? (
                  <ArrayDiff
                    beforeValue={beforeVal}
                    afterValue={afterVal}
                    depth={1}
                    attachmentLabels={attachmentLabels}
                    labels={labels}
                    fieldName={key}
                  />
                ) : (
                  <PrimitiveDiff
                    beforeValue={beforeVal}
                    afterValue={afterVal}
                    fieldName={key}
                    labels={labels}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
